import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, MapPin, BarChart3, Users, Briefcase, FileEdit, ShoppingBag,
  UtensilsCrossed, Settings, LogOut, Home, ChevronDown, ClipboardCheck, Truck, UserCircle2,
  GraduationCap, Star, Menu, X,
} from "lucide-react";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isSuper = user?.role === "super_admin";
  const isAdmin = isSuper || user?.role === "admin";
  const [stores, setStores] = useState([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (isAdmin) api.get("/stores").then((r) => setStores(r.data)).catch(() => {});
  }, [isAdmin]);

  const NAV = useMemo(() => [
    { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", show: true },
    { to: "/admin/stores", icon: MapPin, label: "Stores", show: isAdmin },
    { to: "/admin/inventory-counts", icon: ClipboardCheck, label: "Inventory Counts", show: isAdmin },
    { to: "/admin/restock-requests", icon: Truck, label: "Restock Requests", show: isAdmin },
    { to: "/admin/customers", icon: UserCircle2, label: "Customers", show: isAdmin },
    { to: "/admin/training", icon: GraduationCap, label: "Training & SOP", show: isAdmin },
    { to: "/admin/feedback", icon: Star, label: "Feedback", show: isAdmin },
    { to: "/admin/reports", icon: BarChart3, label: "Company Reports", show: isAdmin },
    { to: "/admin/orders", icon: ShoppingBag, label: "Orders", show: isAdmin },
    { to: "/admin/employees", icon: Users, label: "Employees", show: isAdmin },
    { to: "/admin/menu-catalog", icon: UtensilsCrossed, label: "Menu Catalog", show: isAdmin },
    { to: "/admin/applications", icon: Briefcase, label: "Applications", show: isAdmin },
    { to: "/admin/careers-content", icon: FileEdit, label: "Careers Content", show: isAdmin },
    { to: "/admin/settings", icon: Settings, label: "Settings", show: isAdmin },
  ].filter((n) => n.show), [isAdmin]);

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      <aside className="hidden w-60 shrink-0 flex-col bg-neutral-950 text-neutral-300 lg:flex">
        <div className="px-6 py-5 border-b border-neutral-900">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center text-white font-display font-bold">S</div>
            <div>
              <div className="font-display font-bold text-white leading-tight">Sandar Sushi</div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                {isSuper ? "Super Admin" : "Admin Console"}
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, end, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-neutral-900 hover:text-white"
                }`}
              data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <Icon className="w-4 h-4"/> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-neutral-900 space-y-1">
          <button onClick={() => nav("/")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-neutral-900 hover:text-white" data-testid="admin-back-site">
            <Home className="w-4 h-4"/> Back to Site
          </button>
          <div className="px-3 py-2 text-xs text-neutral-500">
            <div className="font-semibold text-neutral-300">{user?.name}</div>
            <div className="capitalize">{user?.role?.replace("_", " ")}</div>
          </div>
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-neutral-900 hover:text-red-400" data-testid="admin-logout">
            <LogOut className="w-4 h-4"/> Logout
          </button>
        </div>
      </aside>

      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="admin-mobile-navigation">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close navigation"
            onClick={() => setMobileNavOpen(false)}
            data-testid="admin-mobile-navigation-overlay"
          />
          <aside className="relative flex h-full w-72 flex-col bg-neutral-950 text-neutral-300 shadow-2xl">
            <div className="flex items-center justify-between border-b border-neutral-900 px-5 py-4">
              <div className="font-display font-bold text-white">Sandar Sushi</div>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="rounded-lg p-2 text-neutral-300 hover:bg-neutral-900"
                aria-label="Close navigation"
                data-testid="admin-mobile-navigation-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
              {NAV.map(({ to, end, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${
                    isActive ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-neutral-900 hover:text-white"
                  }`}
                  data-testid={`admin-mobile-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        {isAdmin && (
          <div className="sticky top-0 z-30 flex items-center justify-between border-b border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur lg:px-10">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="rounded-lg border border-neutral-200 p-2 text-neutral-700 lg:hidden"
              aria-label="Open navigation"
              data-testid="admin-mobile-navigation-open"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="relative">
              <button
                onClick={() => setSwitcherOpen((o) => !o)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 text-sm font-medium"
                data-testid="store-switcher"
              >
                <MapPin className="w-3.5 h-3.5 text-[hsl(var(--primary))]"/>All Stores
                <ChevronDown className="w-3.5 h-3.5 text-neutral-400"/>
              </button>
              {switcherOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-neutral-200 shadow-lg py-1 z-50" data-testid="store-switcher-menu">
                  <button onClick={() => { setSwitcherOpen(false); nav("/admin/stores"); }} className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 font-medium">All Stores</button>
                  <div className="border-t border-neutral-100 my-1"/>
                  {stores.map((s) => (
                    <button key={s.id} onClick={() => { setSwitcherOpen(false); nav(`/admin/stores/${s.id}`); }} className="w-full px-4 py-2 text-left text-sm hover:bg-neutral-50 flex items-center justify-between" data-testid={`store-switcher-item-${s.id}`}>
                      <span>{s.name}</span>
                      <span className="text-[10px] text-neutral-400">{s.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
